// Extracted from original/src/App.tsx:43634-44801 (`activeTab === "audit_control"`, ADR-08 §2.1 "copy, then cut logic"):
//   sub-tab selector "سجل تدقيق القيود" / "سجل تدقيق إدارة العملاء" / "عرض قوائم الشراء للمخزن"   :43642-43683
//   movement audit: filters (employee, action, dates) + print / PDF / WhatsApp                      :43684-43784
//   movement audit table: header, rows, expanded "مقارنة تفصيلية", empty row                        :43785-44181
//   customer audit: filters (native selects, dates) + exports                                        :44182-44312
//   customer audit table, badges per action, empty row                                               :44313-44478
//   purchase lists: 4 stat cards, filters, exports, 9-column table, empty row                        :44479-44799
// Kept: every class string, icon and size, the motion frame, the tab buttons (blue active / white idle), the filter
// cards, the counter pills ("سجل تدقيق" amber / blue, "حركة مسجلة"), the column order and cell formats (`#` entry
// numbers, the mono amount line "القيمة المحذوفة:" / "المبلغ القديم: … ← الجديد:", " د.ع" suffixes, "+qty قطعة",
// the "مادة جديدة" / "تحديث رصيد" badge, "بدون خصم نقدي"), the comparison panels (rose original / emerald updated /
// dashed "deleted" panel with the bouncing trash icon), the empty-row texts.
// Cut: `filteredAuditLogs` / `filteredCustomerAuditLogs` / `filteredPurchaseOrders` (App.tsx:7456-7581: UTC filters,
// the loaded journal page only, R-A6, R-A7) → the server's `/audit/*` read model in the office time zone (D-135, D-115),
// passed in as preformatted rows; the per-row amount / change computations (:43827-43899, old_data / new_data of a
// local record) → the container, from the journal rows before / after each correction; `handleUndoAudit`
// (:7620-8205, R-A3) → `onUndo` (the journal's undo route: permission + PIN + reason, a new record, D-093);
// `handlePrint*` (:23219-23498) → `onExport` (the container prints the whole filtered set, D-135).
// Changed by decision: tab gates by token, not role (D-135; old: role admin / Super for the customer audit, nothing
// for the purchase lists, R-A8); the action filter adds "تراجع" (an undo is a record of its own, D-093) and an undo row
// carries its own amber badge; an undone correction is marked; the undo button only where the caller may undo it
// (D-093); amounts in their own currency (USD shown as "$", never 0, R-A6); the customer audit adds "إنشاء حساب" /
// "استعادة حساب" (D-134 coverage) and shows which fields changed, never the personal data (ADR-03 §2.7).
// Added (owner sign-off on their look, D-064 / D-135): the two sub-tabs "سجل الأمان والدخول" and
// "سجل الإعدادات والصلاحيات" in the customer-audit design (short tab labels "الأمان والدخول" / "الصلاحيات" so the five tabs
// stay on the old single row at 1440 px; the full names head their tables and prints); the integrity banner when the watchdog finds the audit chain
// or the ledger broken (D-136, T-37); data-testid attributes.
import React from 'react';
import { motion } from 'motion/react';
import { AlertTriangle, ChevronDown, ChevronUp, FileText, KeyRound, Printer, RotateCcw, Settings, Share2, ShieldCheck, ShoppingCart, Trash2, Users } from 'lucide-react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';

export type AuditTab = 'movements' | 'customers' | 'purchases' | 'security' | 'settings';
export type AuditExport = 'print' | 'pdf' | 'whatsapp';
export type AuditMovementAction = 'cancel' | 'edit' | 'undo' | 'undo_edit';
export type AuditCustomerAction = 'edit_name' | 'delete_account' | 'account_transfer' | 'currency_conversion' | 'create_account' | 'restore_account';
export type AuditLogTone = 'blue' | 'rose' | 'amber' | 'emerald' | 'indigo';

/** The nine compared fields of the old "مقارنة تفصيلية" (:43901-43970), preformatted; null = the side does not exist. */
export interface AuditCompare {
  readonly id: string;
  readonly item: string;
  readonly c_in: string;
  readonly c_out: string;
  readonly currency: string;
  readonly wallet_name: string;
  readonly customer_name: string;
  readonly note: string;
  readonly operator_name: string;
}
export const AUDIT_COMPARE_FIELDS: ReadonlyArray<keyof AuditCompare> = ['id', 'item', 'c_in', 'c_out', 'currency', 'wallet_name', 'customer_name', 'note', 'operator_name'];

export interface AuditMovementRow {
  readonly id: string;
  /** The entry code of the affected operation, without "#". */
  readonly entryCode: string;
  readonly action: AuditMovementAction;
  readonly affected: string;
  /** Change descriptions, joined by " | " (old :43846-43899). */
  readonly changes: readonly string[];
  readonly oldAmount: string;
  readonly newAmount: string;
  readonly changedBy: string;
  readonly changedAt: string;
  readonly reason: string | null;
  readonly undone: boolean;
  readonly canUndo: boolean;
  readonly before: AuditCompare | null;
  readonly after: AuditCompare | null;
}

export interface AuditCustomerRow {
  readonly id: string;
  readonly action: AuditCustomerAction;
  readonly customerName: string;
  readonly details: string;
  /** Mono second line (old conversion balances), optional. */
  readonly subDetails: string | null;
  readonly changedBy: string;
  readonly changedAt: string;
}

export interface AuditPurchaseRow {
  readonly key: string;
  readonly entry: string;
  readonly product: string;
  readonly newItem: boolean;
  readonly note: string | null;
  readonly warehouse: string;
  readonly quantity: string;
  readonly unitCost: string;
  readonly totalCost: string;
  readonly box: string | null;
  readonly operator: string;
  readonly at: string;
}

export interface AuditPurchaseTotals {
  readonly entries: string;
  readonly quantity: string;
  readonly cost: string;
  readonly paid: string;
}

export interface AuditLogRow {
  readonly id: string;
  readonly kindLabel: string;
  readonly tone: AuditLogTone;
  readonly user: string;
  readonly entity: string;
  readonly details: string;
  readonly device: string;
  readonly branch: string;
  readonly at: string;
}

export interface AuditFilters {
  readonly employee: string;
  readonly action: string;
  readonly from: string;
  readonly to: string;
  /** purchases only */
  readonly product?: string;
  readonly warehouse?: string;
}

export interface AuditControlProps {
  readonly tab: AuditTab;
  readonly onTabChange: (t: AuditTab) => void;
  /** Visible sub-tabs (D-135 tokens). */
  readonly tabs: ReadonlySet<AuditTab>;
  readonly integrityBroken: { readonly problems: number; readonly checkedAt: string } | null;
  readonly employees: ReadonlyArray<{ readonly id: string; readonly username: string; readonly manager: boolean }>;
  readonly filters: AuditFilters;
  readonly onFilters: (f: AuditFilters) => void;
  readonly onExport: (tab: AuditTab, how: AuditExport) => void;
  // movements
  readonly movementRows: readonly AuditMovementRow[];
  readonly onUndo: (id: string) => void;
  // customers
  readonly customerRows: readonly AuditCustomerRow[];
  // purchases
  readonly purchaseRows: readonly AuditPurchaseRow[];
  readonly purchaseTotals: AuditPurchaseTotals;
  readonly products: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly warehouses: ReadonlyArray<{ readonly id: string; readonly name: string; readonly main: boolean }>;
  // security / settings
  readonly logRows: readonly AuditLogRow[];
  readonly logKinds: ReadonlyArray<{ readonly id: string; readonly label: string }>;
}

const TONE: Record<AuditLogTone, string> = {
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
};

export function AuditControl(props: AuditControlProps) {
  const { t } = useI18n();
  const auditControlTab = props.tab;
  const f = props.filters;
  const set = (patch: Partial<AuditFilters>) => props.onFilters({ ...f, ...patch });
  const [expandedAuditId, setExpandedAuditId] = React.useState<string | null>(null);
  const tabBtn = (active: boolean) =>
    `px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${active ? 'bg-blue-600 text-white shadow-lg' : 'bg-white dark:bg-[#0d1117] text-gray-500 dark:text-[#8b949e] border border-gray-100 dark:border-[#30363d]'}`;
  const employeeOptions = (headerAll: string): SelectOption[] => [
    { id: '', label: headerAll },
    ...props.employees.map((u) => ({ id: u.id, label: u.username, badge: u.manager ? t('ac.badge_admin') : t('ac.badge_staff') })),
  ];

  const exportButtons = (tab: AuditTab, disabled: boolean, pad: 'p-3' | 'p-2.5') => (
    <div className="flex gap-1.5 shrink-0">
      <button
        type="button"
        onClick={() => props.onExport(tab, 'print')}
        disabled={disabled}
        data-testid={`ac-print-${tab}`}
        className={`${pad} bg-blue-600/10 text-blue-600 dark:text-blue-400 hover:bg-blue-600/20 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
        title={t('ac.print_log')}
      >
        <Printer size={18} />
      </button>
      <button
        type="button"
        onClick={() => props.onExport(tab, 'pdf')}
        disabled={disabled}
        className={`${pad} bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600/20 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
        title={t('ac.save_pdf')}
      >
        <FileText size={18} />
      </button>
      <button
        type="button"
        onClick={() => props.onExport(tab, 'whatsapp')}
        disabled={disabled}
        className={`${pad} bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600/20 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
        title={t('ac.share_whatsapp')}
      >
        <Share2 size={18} />
      </button>
    </div>
  );

  const datePair = (labelCls: string, wrapCls: string, pickerCls: string) => (
    <>
      <div className={wrapCls}>
        <label className={labelCls}>{t('ac.from_date')}</label>
        <EnglishDatePicker value={f.from} onChange={(val) => set({ from: val })} className={pickerCls} />
      </div>
      <div className={wrapCls}>
        <label className={labelCls}>{t('ac.to_date')}</label>
        <EnglishDatePicker value={f.to} onChange={(val) => set({ to: val })} className={pickerCls} />
      </div>
    </>
  );
  const dateLabelCls = 'block text-sm font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest';
  const selectCls = 'w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-bold';

  /** The two new sub-tabs (D-135) in the customer-audit design. */
  const logTab = (tab: 'security' | 'settings') => (
    <div className="space-y-6 animate-fade-in text-right" dir="rtl" data-testid={`ac-panel-${tab}`}>
      <div className="bg-white dark:bg-[#0d1117] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] flex flex-wrap gap-4 items-end dark:digital-border">
        <div className="flex-1 min-w-[200px]">
          <CustomSelect
            label={t('ac.select_employee')}
            options={employeeOptions(t('ac.all_employees'))}
            value={f.employee}
            onChange={(val) => set({ employee: String(val) })}
            headerTitle={t('ac.employees_header')}
            placeholder={t('ac.all_employees')}
            searchable={true}
            size="sm"
          />
        </div>
        <div className="w-full sm:w-48 flex-1 min-w-[130px]">
          <CustomSelect
            label={t('ac.action_type')}
            options={[{ id: '', label: t('ac.all') }, ...props.logKinds.map((k) => ({ id: k.id, label: k.label }))]}
            value={f.action}
            onChange={(val) => set({ action: String(val) })}
            headerTitle={t('ac.action_header')}
            searchable={true}
            size="sm"
          />
        </div>
        {datePair(dateLabelCls, 'w-full sm:w-48 flex-1 min-w-[130px]', '!py-2')}
        {exportButtons(tab, props.logRows.length === 0, 'p-3')}
      </div>
      <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden dark:digital-border">
        <div className="p-5 border-b border-gray-150 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#161b22]/50 flex justify-between items-center">
          <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc]">{t(tab === 'security' ? 'ac.security_table_title' : 'ac.settings_table_title')}</h4>
          <span className="text-xs font-bold text-blue-500 bg-blue-500/10 px-2.5 py-1 rounded-full font-mono">
            {props.logRows.length} {t('ac.records')}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-right" dir="rtl">
            <thead>
              <tr className="bg-gray-50/70 dark:bg-[#161b22]/30 border-b border-gray-150 dark:border-[#30363d] text-gray-400 dark:text-[#8b949e] text-xs font-bold">
                <th className="p-4">{t('ac.col_action')}</th>
                <th className="p-4">{t('ac.col_user')}</th>
                <th className="p-4">{t('ac.col_entity')}</th>
                <th className="p-4">{t('ac.col_log_details')}</th>
                <th className="p-4">{t('ac.col_device')}</th>
                <th className="p-4">{t('ac.col_branch')}</th>
                <th className="p-4">{t('ac.col_datetime')}</th>
              </tr>
            </thead>
            <tbody>
              {props.logRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-400 dark:text-[#8b949e] italic">
                    {t('ac.log_empty')}
                  </td>
                </tr>
              ) : (
                props.logRows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors" data-testid="ac-log-row">
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${TONE[r.tone]}`}>{r.kindLabel}</span>
                    </td>
                    <td className="p-4 font-bold dark:text-[#f0f6fc]">{r.user}</td>
                    <td className="p-4 dark:text-[#f0f6fc]">{r.entity}</td>
                    <td className="p-4 text-xs dark:text-[#f0f6fc]">{r.details}</td>
                    <td className="p-4 text-gray-400 dark:text-[#8b949e] font-mono text-[10px] max-w-[220px] truncate" title={r.device}>
                      {r.device}
                    </td>
                    <td className="p-4 text-gray-500 dark:text-[#8b949e] font-bold">{r.branch}</td>
                    <td className="p-4 text-gray-400 dark:text-[#8b949e] font-mono text-xs">{r.at}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div key="audit_control" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6" data-testid="audit-screen">
      {props.integrityBroken && (
        <div className="premium-card p-6 border-l-4 border-l-rose-500 flex items-start gap-3 text-right" dir="rtl" data-testid="ac-integrity-broken">
          <AlertTriangle className="text-rose-500 shrink-0" size={22} />
          <div>
            <h4 className="text-sm font-black text-rose-600 dark:text-rose-400">{t('ac.integrity_title')}</h4>
            <p className="text-xs font-bold text-gray-500 dark:text-[#8b949e] mt-1">{t('ac.integrity_body', { n: props.integrityBroken.problems, at: props.integrityBroken.checkedAt })}</p>
          </div>
        </div>
      )}
      {/* Tab Selector inside Audit & Control section */}
      <div className="flex flex-wrap gap-2 mb-4 text-right" dir="rtl">
        {props.tabs.has('movements') && (
          <button type="button" data-testid="ac-tab-movements" onClick={() => props.onTabChange('movements')} className={tabBtn(auditControlTab === 'movements')}>
            <ShieldCheck size={18} />
            {t('ac.tab_movements')}
          </button>
        )}
        {props.tabs.has('customers') && (
          <button type="button" data-testid="ac-tab-customers" onClick={() => props.onTabChange('customers')} className={tabBtn(auditControlTab === 'customers')}>
            <Users size={18} />
            {t('ac.tab_customers')}
          </button>
        )}
        {props.tabs.has('purchases') && (
          <button type="button" data-testid="ac-tab-purchases" onClick={() => props.onTabChange('purchases')} className={tabBtn(auditControlTab === 'purchases')}>
            <ShoppingCart size={18} />
            {t('ac.tab_purchases')}
          </button>
        )}
        {props.tabs.has('security') && (
          <button type="button" data-testid="ac-tab-security" onClick={() => props.onTabChange('security')} className={tabBtn(auditControlTab === 'security')}>
            <KeyRound size={18} />
            {t('ac.tab_security_short')}
          </button>
        )}
        {props.tabs.has('settings') && (
          <button type="button" data-testid="ac-tab-settings" onClick={() => props.onTabChange('settings')} className={tabBtn(auditControlTab === 'settings')}>
            <Settings size={18} />
            {t('ac.tab_settings_short')}
          </button>
        )}
      </div>
      {/* RENDER ACTIVE AUDIT CONTROL TAB */}
      {auditControlTab === 'movements' && props.tabs.has('movements') && (
        <div className="space-y-6 animate-fade-in text-right" dir="rtl" data-testid="ac-panel-movements">
          {/* Audit filters */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] flex flex-wrap gap-4 items-end dark:digital-border">
            <div className="flex-1 min-w-[200px]">
              <CustomSelect
                label={t('ac.select_employee')}
                options={employeeOptions(t('ac.all_employees'))}
                value={f.employee}
                onChange={(val) => set({ employee: String(val) })}
                headerTitle={t('ac.employees_header')}
                placeholder={t('ac.all_employees')}
                searchable={true}
                size="sm"
              />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[130px]">
              <CustomSelect
                label={t('ac.action_type')}
                options={[
                  { id: '', label: t('ac.all') },
                  { id: 'edit', label: t('ac.action_edit') },
                  { id: 'delete', label: t('ac.action_delete') },
                  { id: 'undo', label: t('ac.action_undo') },
                ]}
                value={f.action}
                onChange={(val) => set({ action: String(val) })}
                headerTitle={t('ac.action_header')}
                searchable={false}
                size="sm"
              />
            </div>
            {datePair(dateLabelCls, 'w-full sm:w-48 flex-1 min-w-[130px]', '!py-2')}
            {/* Actions */}
            {exportButtons('movements', props.movementRows.length === 0, 'p-3')}
          </div>
          {/* Audit logs table */}
          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden dark:digital-border">
            <div className="p-5 border-b border-gray-150 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#161b22]/50 flex justify-between items-center">
              <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc]">{t('ac.mov_table_title')}</h4>
              <span className="text-xs font-bold text-amber-500 bg-amber-500/10 px-2.5 py-1 rounded-full font-mono">
                {props.movementRows.length} {t('ac.records')}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-right" dir="rtl">
                <thead>
                  <tr className="bg-gray-50/70 dark:bg-[#161b22]/30 border-b border-gray-150 dark:border-[#30363d] text-gray-400 dark:text-[#8b949e] text-xs font-bold">
                    <th className="p-4">{t('ac.col_entry')}</th>
                    <th className="p-4">{t('ac.col_audit_action')}</th>
                    <th className="p-4">{t('ac.col_affected')}</th>
                    <th className="p-4">{t('ac.col_details_amount')}</th>
                    <th className="p-4">{t('ac.col_changed_by')}</th>
                    <th className="p-4">{t('ac.col_changed_at')}</th>
                    <th className="p-4">{t('ac.col_undo')}</th>
                  </tr>
                </thead>
                <tbody>
                  {props.movementRows.map((log) => {
                    const isExpanded = expandedAuditId === log.id;
                    const isCancel = log.action === 'cancel';
                    const isUndo = log.action === 'undo' || log.action === 'undo_edit';
                    return (
                      <React.Fragment key={log.id}>
                        <tr className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors" data-testid="ac-mov-row">
                          <td className="p-4 font-mono font-bold text-gray-400">#{log.entryCode}</td>
                          <td className="p-4">
                            <span
                              className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${isCancel ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : isUndo ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}
                            >
                              {isCancel ? t('ac.badge_delete') : isUndo ? t('ac.badge_undo') : t('ac.badge_edit')}
                            </span>
                            {log.undone && <span className="ms-1 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-slate-100 dark:bg-slate-800 text-slate-500" data-testid="ac-undone">{t('ac.undone')}</span>}
                          </td>
                          <td className="p-4 font-black text-gray-900 dark:text-[#f0f6fc]">{log.affected}</td>
                          <td className="p-4 text-xs font-bold text-gray-500 dark:text-[#8b949e]">
                            <div className="flex flex-col gap-1">
                              <span className="font-semibold text-gray-800 dark:text-gray-300">{log.changes.join(' | ')}</span>
                              <span className="text-[10px] text-gray-400 font-mono">
                                {isCancel ? `${t('ac.deleted_value')} ${log.oldAmount}` : `${t('ac.old_amount')} ${log.oldAmount} ← ${t('ac.new_amount')} ${log.newAmount}`}
                              </span>
                              {log.reason && <span className="text-[10px] text-gray-400">{t('ac.reason', { reason: log.reason })}</span>}
                            </div>
                          </td>
                          <td className="p-4 font-bold text-gray-600 dark:text-[#8b949e]">{log.changedBy}</td>
                          <td className="p-4 font-mono text-xs text-gray-400 dark:text-[#8b949e]">{log.changedAt}</td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {log.canUndo && (
                                <button
                                  type="button"
                                  data-testid="ac-undo"
                                  onClick={() => props.onUndo(log.id)}
                                  className="p-1.5 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 rounded-lg transition-colors cursor-pointer"
                                  title={t('ac.undo_title')}
                                >
                                  <RotateCcw size={14} className="animate-spin-slow" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setExpandedAuditId(isExpanded ? null : log.id)}
                                className="p-1.5 text-gray-400 hover:text-gray-650 dark:hover:text-[#f0f6fc] hover:bg-gray-50 dark:hover:bg-[#30363d] rounded-lg transition-colors cursor-pointer"
                              >
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="bg-gray-50/50 dark:bg-[#161b22]/20 p-6 border-b border-gray-100 dark:border-[#30363d]">
                              <div className="max-w-4xl mx-auto space-y-4 animate-slide-down">
                                <h5 className="text-xs font-black uppercase tracking-wider text-gray-400 mb-4">{t('ac.compare_title')}</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="p-4 bg-rose-500/5 rounded-2xl border border-rose-500/10 space-y-3">
                                    <div className="flex justify-between items-center pb-2 border-b border-rose-500/10">
                                      <span className="text-xs font-black text-rose-600 uppercase tracking-widest">{t('ac.compare_original')}</span>
                                      <span className="text-[10px] font-bold text-gray-400">OPERATOR: {log.before?.operator_name || '-'}</span>
                                    </div>
                                    <div className="space-y-2 text-xs">
                                      {AUDIT_COMPARE_FIELDS.map((k) => (
                                        <div key={k} className="flex justify-between">
                                          <span className="text-gray-400">{t(`ac.cmp_${k}`)}</span>
                                          <span className="font-mono font-bold text-gray-700 dark:text-gray-300">{log.before?.[k] ?? '-'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  {log.after ? (
                                    <div className="p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10 space-y-3">
                                      <div className="flex justify-between items-center pb-2 border-b border-emerald-500/10">
                                        <span className="text-xs font-black text-emerald-600 uppercase tracking-widest">{t(isUndo ? 'ac.compare_restored' : 'ac.compare_updated')}</span>
                                        <span className="text-[10px] font-bold text-gray-400">BY: {log.changedBy}</span>
                                      </div>
                                      <div className="space-y-2 text-xs">
                                        {AUDIT_COMPARE_FIELDS.map((k) => {
                                          const isChanged = (log.before?.[k] ?? '-') !== log.after![k];
                                          return (
                                            <div key={k} className={`flex justify-between ${isChanged ? 'text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-500/5 px-1 py-0.5 rounded' : ''}`}>
                                              <span className="text-gray-400">{t(`ac.cmp_${k}`)}</span>
                                              <span className="font-mono">{log.after![k]}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="p-4 bg-gray-50 dark:bg-[#161b22]/40 rounded-2xl border border-dashed border-gray-200 dark:border-[#30363d] flex flex-col items-center justify-center text-center space-y-2">
                                      <Trash2 size={24} className="text-rose-500/75 animate-bounce-slow" />
                                      <p className="text-xs font-bold text-gray-500">{t('ac.compare_deleted')}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {props.movementRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-gray-400 dark:text-[#8b949e] italic">
                        {t('ac.mov_empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {auditControlTab === 'customers' && props.tabs.has('customers') && (
        <div className="space-y-6 animate-fade-in text-right" dir="rtl" data-testid="ac-panel-customers">
          {/* Customer Audit filters */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] flex flex-wrap gap-4 items-end dark:digital-border">
            <div className="flex-1 min-w-[200px]">
              <label className={dateLabelCls}>{t('ac.select_employee')}</label>
              <select value={f.employee} onChange={(e) => set({ employee: e.target.value })} className={selectCls}>
                <option value="">{t('ac.all_employees')}</option>
                {props.employees.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[130px]">
              <label className={dateLabelCls}>{t('ac.action_type')}</label>
              <select value={f.action} onChange={(e) => set({ action: e.target.value })} className={selectCls}>
                <option value="">{t('ac.all')}</option>
                <option value="edit_name">{t('ac.cust_edit_name')}</option>
                <option value="delete_account">{t('ac.cust_delete_account')}</option>
                <option value="account_transfer">{t('ac.cust_account_transfer')}</option>
                <option value="currency_conversion">{t('ac.cust_currency_conversion')}</option>
                <option value="create_account">{t('ac.cust_create_account')}</option>
                <option value="restore_account">{t('ac.cust_restore_account')}</option>
              </select>
            </div>
            {datePair(dateLabelCls, 'w-full sm:w-48 flex-1 min-w-[130px]', '!py-2')}
            {/* Actions */}
            {exportButtons('customers', props.customerRows.length === 0, 'p-3')}
          </div>
          {/* Customer Audit Table */}
          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden dark:digital-border">
            <div className="p-5 border-b border-gray-150 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#161b22]/50 flex justify-between items-center">
              <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc]">{t('ac.cust_table_title')}</h4>
              <span className="text-xs font-bold text-blue-500 bg-blue-500/10 px-2.5 py-1 rounded-full font-mono">
                {props.customerRows.length} {t('ac.records')}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-right" dir="rtl">
                <thead>
                  <tr className="bg-gray-50/70 dark:bg-[#161b22]/30 border-b border-gray-150 dark:border-[#30363d] text-gray-400 dark:text-[#8b949e] text-xs font-bold">
                    <th className="p-4">{t('ac.col_action')}</th>
                    <th className="p-4">{t('ac.col_customer')}</th>
                    <th className="p-4">{t('ac.col_cust_details')}</th>
                    <th className="p-4">{t('ac.col_operator')}</th>
                    <th className="p-4">{t('ac.col_datetime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {props.customerRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-gray-400 dark:text-[#8b949e] italic">
                        {t('ac.cust_empty')}
                      </td>
                    </tr>
                  ) : (
                    props.customerRows.map((log) => {
                      const actionClass =
                        log.action === 'edit_name'
                          ? TONE.amber
                          : log.action === 'delete_account'
                            ? TONE.rose
                            : log.action === 'account_transfer'
                              ? TONE.indigo
                              : log.action === 'currency_conversion' || log.action === 'create_account'
                                ? TONE.emerald
                                : TONE.blue;
                      return (
                        <tr key={log.id} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors" data-testid="ac-cust-row">
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${actionClass}`}>{t(`ac.cust_${log.action}`)}</span>
                          </td>
                          <td className="p-4 font-bold dark:text-[#f0f6fc]">{log.customerName}</td>
                          <td className="p-4 dark:text-[#f0f6fc]">
                            {log.subDetails ? (
                              <div className="flex flex-col gap-1">
                                <span>{log.details}</span>
                                <span className="text-[10px] text-gray-400 font-mono">{log.subDetails}</span>
                              </div>
                            ) : (
                              <span>{log.details}</span>
                            )}
                          </td>
                          <td className="p-4 text-gray-500 dark:text-[#8b949e] font-bold">{log.changedBy}</td>
                          <td className="p-4 text-gray-400 dark:text-[#8b949e] font-mono text-xs">{log.changedAt}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {auditControlTab === 'purchases' && props.tabs.has('purchases') && (
        <div className="space-y-6 animate-fade-in text-right" dir="rtl" data-testid="ac-panel-purchases">
          {/* Summary Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm">
              <div className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1">{t('ac.po_total_entries')}</div>
              <div className="text-2xl font-black text-blue-600 dark:text-blue-400 font-mono">
                {props.purchaseTotals.entries}
                <span className="text-xs font-bold text-gray-500 mr-1.5">{t('ac.po_entries_unit')}</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm">
              <div className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1">{t('ac.po_total_qty')}</div>
              <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                {props.purchaseTotals.quantity}
                <span className="text-xs font-bold text-gray-500 mr-1.5">{t('ac.po_pcs')}</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm">
              <div className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1">{t('ac.po_total_cost')}</div>
              <div className="text-2xl font-black text-rose-600 dark:text-rose-400 font-mono">
                {props.purchaseTotals.cost}
                <span className="text-xs font-bold text-gray-500 mr-1.5">د.ع</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl border border-gray-100 dark:border-[#30363d] shadow-sm">
              <div className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1">{t('ac.po_paid')}</div>
              <div className="text-2xl font-black text-amber-600 dark:text-amber-400 font-mono">
                {props.purchaseTotals.paid}
                <span className="text-xs font-bold text-gray-500 mr-1.5">د.ع</span>
              </div>
            </div>
          </div>

          {/* Filters bar */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] flex flex-wrap gap-4 items-end dark:digital-border">
            <div className="flex-1 min-w-[170px]">
              <CustomSelect
                label={t('ac.po_select_product')}
                options={[{ id: '', label: t('ac.po_all_products') }, ...props.products.map((p) => ({ id: p.id, label: p.name }))]}
                value={f.product ?? ''}
                onChange={(val) => set({ product: String(val) })}
                headerTitle={t('ac.po_products_header')}
                placeholder={t('ac.po_all_products')}
                searchable={true}
                size="sm"
              />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[140px]">
              <CustomSelect
                label={t('ac.po_warehouse')}
                options={[{ id: '', label: t('ac.po_all_warehouses') }, ...props.warehouses.map((w) => ({ id: w.id, label: w.name, ...(w.main ? { badge: t('ac.po_default') } : {}) }))]}
                value={f.warehouse ?? ''}
                onChange={(val) => set({ warehouse: String(val) })}
                headerTitle={t('ac.po_warehouses_header')}
                placeholder={t('ac.po_all_warehouses')}
                searchable={true}
                size="sm"
              />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[140px]">
              <CustomSelect
                label={t('ac.po_employee')}
                options={employeeOptions(t('ac.all_employees'))}
                value={f.employee}
                onChange={(val) => set({ employee: String(val) })}
                headerTitle={t('ac.po_staff_header')}
                placeholder={t('ac.all_employees')}
                searchable={true}
                size="sm"
              />
            </div>
            {datePair('block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest', 'w-full sm:w-40 flex-1 min-w-[120px]', '!py-2 !text-xs')}
            {/* Export buttons */}
            {exportButtons('purchases', props.purchaseRows.length === 0, 'p-2.5')}
          </div>
          {/* Table View */}
          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden dark:digital-border">
            <div className="p-5 border-b border-gray-150 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#161b22]/50 flex justify-between items-center">
              <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2">
                <ShoppingCart size={18} className="text-blue-600 dark:text-blue-400" />
                {t('ac.po_table_title')}
              </h4>
              <span className="text-xs font-bold text-blue-500 bg-blue-500/10 px-3 py-1 rounded-full font-mono">
                {props.purchaseRows.length} {t('ac.po_records')}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-right" dir="rtl">
                <thead>
                  <tr className="bg-gray-50/70 dark:bg-[#161b22]/30 border-b border-gray-150 dark:border-[#30363d] text-gray-400 dark:text-[#8b949e] text-xs font-bold">
                    <th className="p-4">{t('ac.col_entry')}</th>
                    <th className="p-4">{t('ac.po_col_product')}</th>
                    <th className="p-4">{t('ac.po_warehouse')}</th>
                    <th className="p-4">{t('ac.po_col_qty')}</th>
                    <th className="p-4">{t('ac.po_col_unit')}</th>
                    <th className="p-4">{t('ac.po_col_total')}</th>
                    <th className="p-4">{t('ac.po_col_box')}</th>
                    <th className="p-4">{t('ac.po_col_operator')}</th>
                    <th className="p-4">{t('ac.col_datetime')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-[#30363d] text-sm">
                  {props.purchaseRows.map((m) => (
                    <tr key={m.key} className="hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors" data-testid="ac-po-row">
                      <td className="p-4 font-mono font-bold text-gray-400">#{m.entry}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-gray-900 dark:text-[#f0f6fc]">{m.product}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${!m.newItem ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>
                            {!m.newItem ? t('ac.po_stock_update') : t('ac.po_new_item')}
                          </span>
                        </div>
                        {m.note && <p className="text-xs text-gray-400 mt-0.5 font-sans italic">{m.note}</p>}
                      </td>
                      <td className="p-4 font-bold text-gray-600 dark:text-[#8b949e]">
                        <span className="bg-gray-100 dark:bg-[#161b22] px-2.5 py-1 rounded-lg text-xs">{m.warehouse}</span>
                      </td>
                      <td className="p-4 font-black text-emerald-600 dark:text-emerald-400 font-mono">
                        +{m.quantity} <span className="text-[11px] font-normal text-gray-400">{t('ac.po_pcs')}</span>
                      </td>
                      <td className="p-4 font-bold text-gray-900 dark:text-[#f0f6fc] font-mono">{m.unitCost} د.ع</td>
                      <td className="p-4 font-black text-rose-600 dark:text-rose-400 font-mono">{m.totalCost} د.ع</td>
                      <td className="p-4 font-bold text-gray-500 dark:text-[#8b949e] text-xs">{m.box || t('ac.po_no_cash')}</td>
                      <td className="p-4 font-bold text-gray-500 dark:text-[#8b949e]">{m.operator || '-'}</td>
                      <td className="p-4 text-gray-400 dark:text-[#8b949e] font-mono text-xs">{m.at}</td>
                    </tr>
                  ))}
                  {props.purchaseRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="p-12 text-center text-gray-400 italic">
                        {t('ac.po_empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {auditControlTab === 'security' && props.tabs.has('security') && logTab('security')}
      {auditControlTab === 'settings' && props.tabs.has('settings') && logTab('settings')}
    </motion.div>
  );
}
